import { camelCase } from 'lodash';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import Tooltip from '../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';
import { selectCreditBalance } from '../../../../ContentEdit/ContentEditSlice';
import GenerateButton from './GenerateButton';
import ReferenceImageUpload from './ReferenceImageUpload';
import { useVideoUI } from './context/VideoEditorContext';
import { PROMPTING_GUIDE_URL } from './utils/constants';

interface AIPromptInputProps {
  onGenerateVideo: () => void;
  suggestions: string[];
  defaultPlaceholder?: string;
}

export interface AIPromptInputRef {
  getPromptValue: () => string;
  setPromptPlaceholderValue: (value: string) => void;
  setPromptValue: (value: string) => void;
}

const MAX_TEXTAREA_LENGTH = 1000;

const AIPromptInput: React.FC<AIPromptInputProps> = ({
  onGenerateVideo,
  suggestions = [],
  defaultPlaceholder = '',
}) => {
  const { promptInputRef, promptText, handleSuggestionSelect } = useVideoUI();
  const creditBalance = useSelector(selectCreditBalance);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [currentLength, setCurrentLength] = useState(promptText?.length || 0);

  const getPromptValue = (): string => {
    return textareaRef.current?.value || '';
  };

  const setPromptValue = (value: string): void => {
    if (textareaRef.current) {
      textareaRef.current.value = value;
      setCurrentLength(value.length);
    }
  };

  const setPromptPlaceholderValue = (value: string): void => {
    if (textareaRef.current) {
      textareaRef.current.placeholder = value;
    }
  };

  useImperativeHandle(promptInputRef, () => ({
    getPromptValue,
    setPromptPlaceholderValue,
    setPromptValue,
  }));

  const handleSuggestionClick = (suggestion: string) => {
    handleSuggestionSelect(suggestion);
    Telemetry.increment(`ai.contentEdit.suggestion.${camelCase(suggestion)}`);
  };

  const isTextTooLong = currentLength > MAX_TEXTAREA_LENGTH;

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentLength(e.target.value.length);
  };

  return (
    <div className="flex flex-col gap-2 w-full flex-shrink-0">
      <div
        className={twMerge(
          'w-full h-full flex flex-col justify-between overflow-hidden bg-gray-900 border          border-white-15 border-gray-700 rounded-3xl text-white',
          isTextTooLong && 'border-red-400'
        )}
      >
        <div
          style={{ backgroundColor: '#13191D' }}
          className="rounded-3xl p-1 flex-1 flex flex-col gap-1 text-white"
        >
          {/* Suggestions Section */}
          <div className="flex gap-2 p-3 items-center">
            <div className="flex items-center gap-1 flex-shrink-0">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                className="text-white m-0"
              >
                Suggestions
              </Text>
              <Tooltip
                description={`For best prompting practices, check out our <a href="${PROMPTING_GUIDE_URL}" target="_blank" style="text-decoration:underline;color:currentColor;font-weight:400" rel="noopener noreferrer">Prompting Guide</a>`}
                descriptionClassName="font-inter text-sm text-gray-1000 whitespace-nowrap"
                variant="light"
                maxWidth="md"
                className="py-2 px-3"
                trigger={
                  <button
                    className="inline-flex flex-shrink-0"
                    type="button"
                    aria-label="Prompting tips"
                  >
                    <img
                      src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/information-circle-outline-white.svg`}
                      alt="Prompting tips"
                      className="w-4 h-4"
                    />
                  </button>
                }
                hasArrow
              />
            </div>
            <div className="flex overflow-x-auto scrollbar-hide gap-2">
              {suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => (
                  <button
                    key={`suggestion-${suggestion}-${index}`}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="flex-shrink-0 bg-gray-800 text-white px-3 py-1 rounded-full text-xs transition-all border border-transparent hover:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-50"
                    aria-label={`Use suggestion: ${suggestion}`}
                  >
                    {suggestion}
                  </button>
                ))
              ) : (
                <Text fontSize="xs" className="text-gray-400 italic m-0">
                  No suggestions available
                </Text>
              )}
            </div>
          </div>

          <div
            style={{ backgroundColor: '#13191D' }}
            className="rounded-2xl p-4 flex flex-col justify-between"
          >
            <div className="flex gap-2 items-start">
              <img
                src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/ai-star.svg`}
                alt="AI prompt"
                className="w-5 h-5 flex-shrink-0"
              />
              <textarea
                style={{ minHeight: '102px' }}
                ref={textareaRef}
                placeholder={
                  defaultPlaceholder ||
                  'Describe the video you want to create...'
                }
                maxLength={MAX_TEXTAREA_LENGTH + 100}
                className="w-full resize-none bg-transparent border-0 outline-none p-0 placeholder-gray-250 text-base"
                onChange={handleTextChange}
                aria-label="Describe the video you want to create"
                aria-invalid={isTextTooLong}
                aria-describedby={isTextTooLong ? 'prompt-error' : undefined}
                defaultValue={promptText}
              />
            </div>

            <div className="ai-prompt-input-bottom-container">
              <div className="flex justify-between w-full items-center gap-4">
                <div className="mr-auto">
                  <ReferenceImageUpload />
                </div>

                <Text
                  fontSize="sm"
                  className="text-gray-250 m-0"
                  fontWeight="regular"
                >
                  {creditBalance.toLocaleString()} credit
                  {creditBalance !== 1 ? 's' : ''} available
                </Text>
              </div>
              <GenerateButton
                disabled={isTextTooLong}
                onGenerateVideo={onGenerateVideo}
                className="w-auto flex-shrink-0"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full">
        {isTextTooLong && (
          <Text
            id="prompt-error"
            className="text-red-100 m-0"
            fontSize="sm"
            fontWeight="regular"
          >
            You&apos;ve reached the maximum allowed length (
            {MAX_TEXTAREA_LENGTH.toLocaleString()} characters)
          </Text>
        )}
      </div>
    </div>
  );
};

export default React.memo(AIPromptInput);
